import { useQuery } from '@tanstack/react-query';
import React, { FC, useMemo, useRef, useState } from 'react';

import { type BankAccountWithDetails, bankAccountGetBankAccount } from '@column/api';
import { Icon, formatNumber } from '@column/column-ui-kit';
import { Popover, PopoverContent, PopoverTrigger } from '@column/ui';

import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { type Action } from '~/lib/authz/eval';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

import {
  type AccountCardRow,
  EmptyAccountTrigger,
  EntitySearchCommand,
  type EntitySearchOption,
  FadeSwap,
  SelectedAccountCard,
  highlightMatch,
  renderMatchFragment,
} from './AccountSelector';
import { FADE_ONLY_MOTION, OVERLAP_CELL, OVERLAP_GRID, POPOVER_CONTENT_CLASS, TRIGGER_HEIGHT_PX } from './constants';

// Fields the row doesn't otherwise show. When the search matched one of these
// (rather than the name or account number), the secondary line reveals it —
// labelled — instead of the account number, so it's clear why the row matched.
const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'ACCOUNT_NUMBERS.ROUTING_NUMBER', label: 'RTN' },
  { field: 'ACCOUNT_NUMBERS.DESCRIPTION', label: 'ACCT DESC' },
  { field: 'ACCOUNT_NUMBERS.ACCOUNT_NUMBER_ID', label: 'ACCT ID' },
  { field: 'BANK_ACCOUNT_ID', label: 'ID' },
];

export interface BankAccountSelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  /** Permission gating which accounts are selectable (unauthorized rows are disabled). */
  permission?: Action['permission'];
  /** Tooltip shown on the lock adornment for accounts the user can't use. */
  unauthorizedText?: string;
  hasError?: boolean;
  /** The transfer amount in cents — used to flag insufficient funds. */
  amount?: number;
  /** Whether funds are pulled from this account (it's the money origin), so balance matters. */
  isSource?: boolean;
  /** `data-cy` hook applied to the field, matching the old FormField wrapper. */
  dataCy?: string;
}

/**
 * A single bank-account picker: a placeholder trigger that morphs into a
 * server-searched dropdown, collapsing into a detail card once an account is
 * chosen. Reusable across transfer forms — wire `value`/`onChange`/`onBlur` to
 * a form field and pass the direction-derived `isSource`/`permission`.
 */
export const BankAccountSelector: FC<BankAccountSelectorProps> = ({
  value,
  onChange,
  onBlur,
  permission,
  unauthorizedText,
  hasError,
  amount,
  isSource,
  dataCy,
}) => {
  const [open, setOpen] = useState(false);
  // Mirrors the search input so option rows can highlight the matched substring.
  const [query, setQuery] = useState('');
  // Set when a selection (not an escape/click-outside) closes the popover, so we
  // can skip Radix's focus-restore — see the PopoverContent's onCloseAutoFocus.
  const selectedOnClose = useRef(false);
  const { isAuthorized } = useSessionStore();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const { bankAccounts, search, handleScrollEnd } = useBankAccountOptions({ value });

  const activeQuery = useQuery({
    queryKey: ['bankAccountDropdown', 'get', platformId, isSandbox, value || undefined],
    queryFn: async () => {
      const { data } = await bankAccountGetBankAccount({ path: { bank_account_id: value }, throwOnError: true });
      return data;
    },
    enabled: Boolean(value),
    placeholderData: () => bankAccounts.find((ba) => ba.id === value),
    staleTime: 5 * 60_000,
    // This GET only enriches data we already have from the dropdown list. A 403
    // here (server-side per-account authorization) is deterministic, so retrying
    // would just spam several seconds of console errors to no effect.
    retry: false,
  });
  // Fall back to the dropdown list entry — itself a full BankAccountWithDetails —
  // so a failed or forbidden detail fetch never blanks the card. placeholderData
  // only applies while the query is pending; once it errors, data is undefined.
  const account = (activeQuery.data ?? bankAccounts.find((ba) => ba.id === value)) as
    | BankAccountWithDetails
    | undefined;

  const options: EntitySearchOption[] = useMemo(
    () =>
      bankAccounts.map((ba) => {
        const authorized = !permission
          ? true
          : isAuthorized({ permission, permissionLevel: 'write', bankAccount: { id: ba.id, owners: undefined } });
        // If the query matched a field the row doesn't show (ID, routing, etc.),
        // reveal that field in place of the account number so the match is visible.
        const matchedHidden = getHighlightedSuffix(ba.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          id: ba.id,
          primary: highlightMatch(ba.display_name || ba.description || 'Unnamed', query),
          // Normally the full account number (monospace, lighter-gray "ACCT" prefix);
          // when a hidden field matched, that labelled field instead.
          secondary: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : ba.default_account_number ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">ACCT </span>
              {highlightMatch(ba.default_account_number, query)}
            </span>
          ) : undefined,
          // Balance right-aligned across from the title, with its label beneath it.
          trailingPrimary: formatNumber(ba.balances?.available_amount ?? ''),
          trailingSecondary: 'Available',
          disabled: !authorized,
          adornment:
            !authorized && unauthorizedText ? (
              <span title={unauthorizedText} className="flex text-muted-foreground">
                <Icon.AnimationLock />
              </span>
            ) : undefined,
        };
      }),
    [bankAccounts, isAuthorized, permission, unauthorizedText, query]
  );

  const availableAmount = account?.balances?.available_amount;
  // When funds are pulled from this account (it's the origin), flag when the
  // entered amount exceeds the available balance.
  const insufficient =
    Boolean(isSource) &&
    typeof amount === 'number' &&
    amount > 0 &&
    typeof availableAmount === 'number' &&
    amount > availableAmount;

  const rows: AccountCardRow[] = account
    ? [
        { label: 'Available balance', value: formatNumber(availableAmount ?? '') },
        { label: 'Account number', value: account.default_account_number || '—', monospaced: true },
        { label: 'Routing number', value: account.routing_number || '—', monospaced: true },
      ]
    : [];

  return (
    <div className={OVERLAP_GRID} data-cy={dataCy}>
      <SelectedAccountCard
        className={OVERLAP_CELL}
        open={Boolean(value)}
        title={account?.display_name || account?.description || 'Bank account'}
        rows={rows}
        isLoading={!account && activeQuery.isLoading}
        hasError={hasError}
        alert={insufficient ? 'Insufficient balance to create this transfer' : undefined}
        onClear={() => onChange('')}
      />
      <FadeSwap visible={!value} className={OVERLAP_CELL}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            // Reset the server search (and the highlight query) so reopening starts
            // from an unfiltered list rather than a stale query the (remounted,
            // empty) input no longer shows.
            if (!next) {
              onBlur();
              setQuery('');
              search('');
            }
          }}
        >
          <PopoverTrigger asChild>
            <EmptyAccountTrigger label="Select a bank account" hasError={hasError} />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={-TRIGGER_HEIGHT_PX}
            className={POPOVER_CONTENT_CLASS}
            {...FADE_ONLY_MOTION}
            onCloseAutoFocus={(event) => {
              // After a selection the field collapses to the (aria-hidden) card,
              // so the trigger Radix would restore focus to is now hidden —
              // focusing it would trap focus in an aria-hidden subtree. Let focus
              // fall back to the document instead.
              if (selectedOnClose.current) {
                selectedOnClose.current = false;
                event.preventDefault();
              }
            }}
          >
            <EntitySearchCommand
              options={options}
              searchPlaceholder="Search bank accounts…"
              emptyMessage="No accounts found"
              onSearch={(q) => {
                setQuery(q);
                search(q);
              }}
              onSelect={(id) => {
                selectedOnClose.current = true;
                onChange(id);
                setOpen(false);
                // Selecting closes via setOpen (not onOpenChange), so reset here too.
                setQuery('');
                search('');
              }}
              onScrollEnd={handleScrollEnd}
            />
          </PopoverContent>
        </Popover>
      </FadeSwap>
    </div>
  );
};
